import {
  Box,
  Breadcrumbs,
  Card,
  CardHeader,
  Link,
  Tabs,
  Typography,
} from "@material-ui/core";
import Tab from "@material-ui/core/Tab";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import TabContext from "@material-ui/lab/TabContext";
import TabPanel from "@material-ui/lab/TabPanel";
import { makeStyles } from "@material-ui/styles";
import clsx from "clsx";
import "prismjs/components/prism-clike";
import "prismjs/components/prism-javascript";
import React, { useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";

import { useAllSaasDestinations } from "../hooks/useAllSaasDestinations";
import Navigation from "../Navigation";
import { DeliveryMetricsChart } from "./deliveryMetrics";
import useDestinationById from "../hooks/useDestinationById";
import { allDestinationsData } from "./destinations";
import { DestinationCardTitle } from "../components/DestinationCardTitle";

function DestinationDeliveryMetrics({ destinationId }) {
  const dest = useDestinationById(destinationId);
  const [chartType, setChartType] = React.useState("ratio");
  const handleChange = (event, newValue) => {
    setChartType(newValue);
  };

  if (!dest) {
    return (
      <div>
        Delivery metrics are only available when the destination is configured.
      </div>
    );
  }

  const destination = allDestinationsData[dest.type_slug];

  return (
    <Card>
      <CardHeader
        title={
          <DestinationCardTitle
            titleSuffix="Delivery Metrics"
            destination={destination}
            configuredDest={dest}
          />
        }
      />
      <div>
        <Box sx={{ width: "100%", typography: "body1" }}>
          <TabContext value={chartType}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={chartType}
                onChange={handleChange}
                aria-label="basic tabs example"
              >
                <Tab label="Success Rate" value="ratio" />
                <Tab label="Breakdown" value="count" />
              </Tabs>
            </Box>
            <TabPanel value="ratio" index={0}>
              <DeliveryMetricsChart destination={dest} chartType="ratio" />
            </TabPanel>
            <TabPanel value="count" index={1}>
              <DeliveryMetricsChart destination={dest} chartType="count" />
            </TabPanel>
          </TabContext>
        </Box>
      </div>
    </Card>
  );
}

const useStyles = makeStyles(() => ({
  navigation: {
    paddingTop: 12,
  },
  card: {
    width: "50%",
    minWidth: 800,
    minHeight: 500,
  },
  lightText: {
    color: "#546E7A",
  },
  darkText: {
    color: "#263238",
  },
  breadcrumb: {
    marginTop: 5,
    marginBottom: 10,
  },
}));

const DestinationDeliveryMetricsWrapper = ({
  destinationSlug,
  destinationId,
}) => {
  const history = useHistory();
  const classes = useStyles();

  const allSaasDestinations = useAllSaasDestinations();
  const destination = useMemo(() => allSaasDestinations[destinationSlug], [
    allSaasDestinations,
    destinationSlug,
  ]);

  const category = useMemo(() => destination.categoryDisplay(), [destination]);
  const goToCategory = useCallback(
    () => history.push(`/destinations/${category}`),
    [category, history]
  );

  const goToDestination = useCallback(
    () =>
      history.push(
        `/destinations/${category}/${destinationSlug}/${destinationId}`
      ),
    [category, history, destinationSlug, destinationId]
  );

  return (
    <Navigation className={clsx(classes.navigation)}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        className={clsx(classes.breadcrumb)}
      >
        <Link color="inherit" onClick={() => history.push(`/destinations`)}>
          <Typography className={clsx(classes.lightText)} variant="overline">
            Destinations
          </Typography>
        </Link>
        <Link color="inherit" onClick={goToCategory}>
          <Typography className={clsx(classes.lightText)} variant="overline">
            {destination.categoryDisplay().toUpperCase()}
          </Typography>
        </Link>
        <Link color="inherit" onClick={goToDestination}>
          <Typography className={clsx(classes.lightText)} variant="overline">
            {destination.name.toUpperCase()}
          </Typography>
        </Link>
        <Typography className={clsx(classes.darkText)} variant="overline">
          Destination Delivery Metrics
        </Typography>
      </Breadcrumbs>
      <DestinationDeliveryMetrics destinationId={destinationId} />
    </Navigation>
  );
};

export default DestinationDeliveryMetricsWrapper;
